import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMunicipalityLocalities } from '../hooks/useMunicipalityLocalities';
import { NO_LOCALITY_VALUE } from '../utils/form-mapping.utils';
import { NO_DATA_LABEL, NO_LOCALITY_LABEL } from '../utils/format.utils';
import type { PreRegisteredAccommodationFormValues } from '../schemas/pre-registered-accommodation.schema';

type TextFieldName = Exclude<
  keyof PreRegisteredAccommodationFormValues,
  'localityId' | 'observations'
>;

interface TextInputFieldProps {
  name: TextFieldName;
  label: string;
  readOnly: boolean;
  required?: boolean;
  maxLength?: number;
  className?: string;
}

const TextInputField: React.FC<TextInputFieldProps> = ({
  name,
  label,
  readOnly,
  required,
  maxLength,
  className,
}) => {
  const { control } = useFormContext<PreRegisteredAccommodationFormValues>();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>
            {label} {required && <span className="text-red-500">*</span>}
          </FormLabel>
          <FormControl>
            <Input {...field} disabled={readOnly} maxLength={maxLength} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

// Places / rooms: empty means "no informado", which is not the same as 0 (see the section hint)
const CountField: React.FC<{ name: 'places' | 'rooms'; label: string; readOnly: boolean }> = ({
  name,
  label,
  readOnly,
}) => {
  const { control } = useFormContext<PreRegisteredAccommodationFormValues>();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input {...field} inputMode="numeric" placeholder={NO_DATA_LABEL} disabled={readOnly} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const FieldSection: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <section className="space-y-3">
    <div>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
  </section>
);

interface PreRegisteredAccommodationFieldsProps {
  // Localities are restricted to the list's municipality
  municipalityId: number;
  readOnly: boolean;
  // Rendered right under the trade name (REPAT matches, KER-568)
  afterEstablishmentName?: React.ReactNode;
}

export const PreRegisteredAccommodationFields: React.FC<PreRegisteredAccommodationFieldsProps> = ({
  municipalityId,
  readOnly,
  afterEstablishmentName,
}) => {
  const { control } = useFormContext<PreRegisteredAccommodationFormValues>();
  const { data: localities = [], isLoading } = useMunicipalityLocalities(municipalityId);

  return (
    <div className="space-y-6">
      <FieldSection title="Establecimiento">
        <TextInputField
          name="establishmentName"
          label="Denominación comercial"
          required
          maxLength={255}
          readOnly={readOnly}
          className="sm:col-span-2"
        />
        {afterEstablishmentName && <div className="sm:col-span-2">{afterEstablishmentName}</div>}
        <FormField
          control={control}
          name="localityId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Localidad</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={readOnly || isLoading}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={NO_LOCALITY_LABEL} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NO_LOCALITY_VALUE}>{NO_LOCALITY_LABEL}</SelectItem>
                  {localities.map((locality) => (
                    <SelectItem key={locality.id} value={String(locality.id)}>
                      {locality.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!readOnly && !isLoading && localities.length === 0 && (
                <FormDescription>El municipio todavía no tiene localidades asignadas.</FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        <TextInputField
          name="establishmentPhone"
          label="Teléfono del establecimiento"
          maxLength={50}
          readOnly={readOnly}
        />
        <TextInputField
          name="address"
          label="Dirección"
          maxLength={255}
          readOnly={readOnly}
          className="sm:col-span-2"
        />
      </FieldSection>

      <FieldSection title="Referente">
        <TextInputField name="contactName" label="Nombre" maxLength={100} readOnly={readOnly} />
        <TextInputField name="contactPhone" label="Teléfono" maxLength={50} readOnly={readOnly} />
      </FieldSection>

      <FieldSection
        title="Capacidad"
        hint={readOnly ? undefined : 'Dejá vacío lo que no fue informado (no es lo mismo que 0).'}
      >
        <CountField name="places" label="Plazas" readOnly={readOnly} />
        <CountField name="rooms" label="Habitaciones" readOnly={readOnly} />
      </FieldSection>

      <FieldSection title="Otros datos">
        <TextInputField
          name="legalInstrument"
          label="Instrumento legal"
          maxLength={500}
          readOnly={readOnly}
          className="sm:col-span-2"
        />
        <FormField
          control={control}
          name="observations"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>Observaciones</FormLabel>
              <FormControl>
                <Textarea {...field} disabled={readOnly} rows={3} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </FieldSection>
    </div>
  );
};
